import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Modal as AntModal, ConfigProvider } from "antd";
import type { ModalProps as AntModalProps } from "antd";
import {
  X,
  Minus,
  Maximize2,
  Minimize2,
  AlertCircle,
  CheckCircle,
  Info,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "../../theme/colors";
import Button from "../button";
import {
  CustomHeader,
  type CustomHeaderProps,
  type HeaderActionButton,
} from "../custom-header/CustomHeader";

// Hook to detect screen size for responsive behavior
const useResponsiveModal = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkSize = () => {
      setIsMobile(window.innerWidth < 480);
      setIsTablet(window.innerWidth >= 480 && window.innerWidth < 768);
    };

    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  return { isMobile, isTablet };
};

// ============================================
// Types
// ============================================

export type ModalSize =
  | "auto"
  | "small"
  | "medium"
  | "large"
  | "extra-large"
  | "full";
export type ModalVariant =
  | "default"
  | "destructive"
  | "info"
  | "success"
  | "warning"
  | "error";
export type ModalViewState = "normal" | "minimized" | "maximized";

export type ModalTitleAlign = "left" | "center" | "right";

/**
 * Props for fullscreen modal header (CustomHeader)
 * These are only used when size="full"
 */
export interface FullscreenHeaderProps {
  /**
   * Handler for back button click in fullscreen modal
   * If not provided, defaults to onClose behavior
   */
  onBack?: () => void;

  /**
   * Label for the back button
   * @default "Back"
   */
  backLabel?: string;

  /**
   * Whether to show the back button
   * @default true
   */
  showBackButton?: boolean;

  /**
   * Custom icon for the back button
   */
  backIcon?: React.ReactNode;

  /**
   * Handler for the action button click
   * If not provided, no action button will be shown
   */
  onAction?: () => void;

  /**
   * Label for the action button
   * Required if onAction is provided
   */
  actionLabel?: React.ReactNode;

  /**
   * Icon to display after the action label
   */
  actionIcon?: React.ReactNode;

  /**
   * Variant for the action button
   * @default "teal"
   */
  actionVariant?: CustomHeaderProps["actionVariant"];

  /**
   * Whether the action is in a loading state
   * @default false
   */
  isLoading?: boolean;

  /**
   * Whether the action button is disabled
   * @default false
   */
  isActionDisabled?: boolean;

  /**
   * Tooltip content for the fullscreen header action button.
   */
  actionTooltip?: React.ReactNode;

  /**
   * Whether to disable the back button
   * @default false
   */
  isBackDisabled?: boolean;

  /**
   * Background color of the fullscreen header
   * @default colors.primarySlate
   */
  headerBackgroundColor?: string;

  /**
   * Whether to use CustomHeader for fullscreen modals
   * @default true (when size="full")
   */
  useCustomHeader?: boolean;

  /**
   * Array of action buttons to display on the right side of the header
   * Takes precedence over single action props (onAction, actionLabel, etc.)
   * Useful when multiple buttons are needed in the header
   */
  actionButtons?: HeaderActionButton[];

  /**
   * Title alignment for fullscreen modal header
   * @default "center"
   */
  fullscreenTitleAlign?: "left" | "center";
}

export interface ModalProps
  extends
    Omit<
      AntModalProps,
      | "title"
      | "footer"
      | "width"
      | "onOk"
      | "onCancel"
      | "okButtonProps"
      | "cancelButtonProps"
    >,
    FullscreenHeaderProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  variant?: ModalVariant;
  /** Alignment of the title and subtitle. Defaults to 'left' */
  titleAlign?: ModalTitleAlign;
  /** Whether to show the header. Defaults to true */
  showHeader?: boolean;
  /** Whether to show border below the header. Defaults to true */
  headerBorder?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode | null | false;
  showFooter?: boolean;
  okText?: string;
  cancelText?: string;
  onOk?: () => void | Promise<void>;
  onCancel?: () => void;
  okButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: string;
    loading?: boolean;
  };
  cancelButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: string;
  };
  confirmLoading?: boolean;
  size?: ModalSize;
  width?: string | number;
  height?: string | number;
  closable?: boolean;
  minimizable?: boolean;
  maximizable?: boolean;
  maskClosable?: boolean;
  keyboard?: boolean;
  centered?: boolean;
  /** Apply horizontal auto margins to the Ant modal container. */
  isCenter?: boolean;
  /** @deprecated Use destroyOnHidden instead */
  destroyOnClose?: boolean;
  destroyOnHidden?: boolean;
  bodyClassName?: string;
  bodyStyle?: React.CSSProperties;
  /** Background color for the modal body */
  bodyBackgroundColor?: string;
  /** Custom padding for the modal body. Overrides default responsive padding */
  bodyPadding?: string | number;
  headerClassName?: string;
  footerClassName?: string;
  loading?: boolean;
  className?: string;
  zIndex?: number;
}

// ============================================
// Constants
// ============================================

const SIZE_MAP: Record<
  ModalSize,
  {
    width: number | string;
    minWidth?: number | string;
    maxWidth?: number | string;
    height?: string;
  }
> = {
  auto: { width: "fit-content", minWidth: 320, maxWidth: "90vw" },
  small: { width: 400 },
  medium: { width: 600 },
  large: { width: 800 },
  "extra-large": { width: 1000 },
  full: { width: "100vw", height: "100vh" },
};

const VARIANT_CONFIG: Record<
  ModalVariant,
  {
    icon: React.ReactNode;
    color: string;
    bgColor: string;
    borderColor: string;
  }
> = {
  default: {
    icon: null,
    color: colors.primary,
    bgColor: colors.primaryExtraLight,
    borderColor: colors.primaryLight,
  },
  destructive: {
    icon: <XCircle size={20} strokeWidth={2} />,
    color: colors.error,
    bgColor: colors.errorBgSubtle,
    borderColor: colors.errorBorder,
  },
  info: {
    icon: <Info size={20} strokeWidth={2} />,
    color: colors.info,
    bgColor: colors.infoBgSubtle,
    borderColor: colors.infoBorder,
  },
  success: {
    icon: <CheckCircle size={20} strokeWidth={2} />,
    color: colors.success,
    bgColor: colors.successBgSubtle,
    borderColor: colors.successBorder,
  },
  warning: {
    icon: <AlertTriangle size={20} strokeWidth={2} />,
    color: colors.warning,
    bgColor: colors.warningBgSubtle,
    borderColor: colors.warningBorder,
  },
  error: {
    icon: <AlertCircle size={20} strokeWidth={2} />,
    color: colors.error,
    bgColor: colors.errorBgSubtle,
    borderColor: colors.errorBorder,
  },
};

// ============================================
// Helper Components
// ============================================

interface IconButtonProps {
  icon: React.ReactNode;
  onClick?: () => void;
  title?: string;
  className?: string;
}

const IconButton: React.FC<IconButtonProps> = ({ icon, onClick, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg transition-colors hover:bg-gray-100"
    style={{ color: colors.textMuted }}
  >
    {icon}
  </button>
);

interface DefaultFooterProps {
  onCancel?: () => void;
  onOk?: () => void;
  cancelText?: string;
  okText?: string;
  confirmLoading?: boolean;
  cancelButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: string;
  };
  okButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: string;
    loading?: boolean;
  };
  variant?: ModalVariant;
}

const DefaultFooter: React.FC<DefaultFooterProps> = ({
  onCancel,
  onOk,
  cancelText = "Cancel",
  okText = "OK",
  confirmLoading = false,
  cancelButtonProps,
  okButtonProps,
  variant = "default",
}) => {
  const isDestructive = variant === "destructive" || variant === "error";
  const isOkDisabled =
    confirmLoading || okButtonProps?.disabled || okButtonProps?.loading;

  return (
    <div className="flex items-center justify-end gap-3">
      {onCancel && (
        <Button
          variant="secondary"
          size="medium"
          onClick={onCancel}
          disabled={confirmLoading || cancelButtonProps?.disabled}
          className={cancelButtonProps?.className}
        >
          {cancelText}
        </Button>
      )}

      {onOk && (
        <Button
          variant={isDestructive ? "danger" : "primary"}
          size="medium"
          onClick={onOk}
          disabled={isOkDisabled}
          loading={confirmLoading || okButtonProps?.loading}
          className={okButtonProps?.className}
          style={
            isDestructive && !isOkDisabled
              ? {
                  backgroundColor: "#c91902",
                  borderColor: "#c91902",
                  color: colors.white,
                  ...okButtonProps?.style,
                }
              : okButtonProps?.style
          }
        >
          {okText}
        </Button>
      )}
    </div>
  );
};

// ============================================
// Main Modal Component
// ============================================

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  variant = "default",
  titleAlign = "left",
  showHeader = true,
  headerBorder = true,
  children,
  footer,
  showFooter = true,
  okText = "OK",
  cancelText = "Cancel",
  onOk,
  onCancel,
  okButtonProps,
  cancelButtonProps,
  confirmLoading = false,
  size = "auto",
  width: customWidth,
  height: customHeight,
  closable = true,
  minimizable = false,
  maximizable = false,
  maskClosable = true,
  keyboard = true,
  centered = true,
  isCenter = false,
  destroyOnClose,
  destroyOnHidden = true,
  bodyClassName,
  bodyStyle,
  bodyBackgroundColor,
  bodyPadding,
  headerClassName,
  footerClassName,
  loading = false,
  className,
  styles: incomingStyles,
  zIndex = 1000,
  // Fullscreen header props
  onBack,
  backLabel = "Back",
  showBackButton = true,
  backIcon,
  onAction,
  actionLabel,
  actionIcon,
  actionVariant = "teal",
  actionTooltip,
  isLoading = false,
  isActionDisabled = false,
  isBackDisabled = false,
  headerBackgroundColor,
  useCustomHeader = true,
  actionButtons,
  fullscreenTitleAlign = "center",
  ...restProps
}) => {
  const [viewState, setViewState] = useState<ModalViewState>("normal");
  const { isMobile, isTablet } = useResponsiveModal();

  const isFullscreen = size === "full";
  const shouldUseCustomHeader = isFullscreen && useCustomHeader;
  const variantConfig = VARIANT_CONFIG[variant];
  const variantIcon = icon || variantConfig.icon;

  // Handle back button for fullscreen modals - falls back to onClose
  const handleBackButton = useCallback(() => {
    if (onBack) {
      onBack();
    } else {
      onClose();
    }
  }, [onBack, onClose]);

  // Render CustomHeader for fullscreen modals
  const renderCustomHeader = useCallback(() => {
    if (!shouldUseCustomHeader) return null;

    return (
      <CustomHeader
        title={title || ""}
        subtitle={typeof subtitle === "string" ? subtitle : undefined}
        onBack={handleBackButton}
        backLabel={backLabel}
        showBackButton={showBackButton}
        backIcon={backIcon}
        onAction={onAction}
        actionLabel={actionLabel}
        actionIcon={actionIcon}
        actionVariant={actionVariant}
        actionTooltip={actionTooltip}
        isLoading={isLoading}
        isActionDisabled={isActionDisabled}
        isBackDisabled={isBackDisabled || isLoading}
        leftContent={icon}
        backgroundColor={headerBackgroundColor}
        actionButtons={actionButtons}
        titleAlign={fullscreenTitleAlign}
      />
    );
  }, [
    shouldUseCustomHeader,
    title,
    subtitle,
    handleBackButton,
    backLabel,
    showBackButton,
    backIcon,
    onAction,
    actionLabel,
    actionIcon,
    actionVariant,
    actionTooltip,
    isLoading,
    isActionDisabled,
    isBackDisabled,
    icon,
    headerBackgroundColor,
    actionButtons,
    fullscreenTitleAlign,
  ]);

  // Calculate dimensions
  const dimensions = useMemo(() => {
    if (viewState === "maximized") {
      return { width: "100vw", height: "100vh" };
    }
    if (viewState === "minimized") {
      return { width: customWidth || SIZE_MAP[size].width, height: "auto" };
    }
    const sizeConfig = SIZE_MAP[size];
    return {
      width: customWidth || sizeConfig.width,
      height: customHeight || sizeConfig.height,
      minWidth: sizeConfig.minWidth,
      maxWidth: sizeConfig.maxWidth,
    };
  }, [viewState, size, customWidth, customHeight]);

  const handleMinimize = useCallback(() => {
    setViewState(viewState === "minimized" ? "normal" : "minimized");
  }, [viewState]);

  const handleMaximize = useCallback(() => {
    setViewState(viewState === "maximized" ? "normal" : "maximized");
  }, [viewState]);

  const handleClose = useCallback(() => {
    setViewState("normal");
    onClose();
  }, [onClose]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      handleClose();
    }
  }, [onCancel, handleClose]);

  const handleOk = useCallback(async () => {
    if (onOk) {
      const result = onOk();
      if (result instanceof Promise) {
        await result;
      }
    }
  }, [onOk]);

  // Custom header with title and subtitle
  const customTitle = useMemo(() => {
    const isCentered = titleAlign === "center";
    const isRightAligned = titleAlign === "right";

    // Title and subtitle content block
    const titleContent = (
      <div
        className={cn(
          "min-w-0 overflow-hidden",
          isCentered ? "flex-1 text-center" : "flex-1",
          isRightAligned && "text-right",
        )}
      >
        {title && (
          <h2
            className={cn(
              "font-semibold leading-snug m-0 truncate",
              isMobile ? "text-base" : "text-lg",
              headerClassName,
            )}
            style={{ color: colors.textDarkest }}
          >
            {title}
          </h2>
        )}
        {subtitle && (
          <p
            className={cn(
              "font-normal leading-relaxed mt-0.5 m-0 wrap-break-words",
              isMobile ? "text-xs" : "text-sm",
            )}
            style={{
              color: colors.textMuted,
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
    );

    // Icon element
    const iconElement = variantIcon ? (
      <div
        className={cn(
          "flex items-center justify-center rounded-full shrink-0",
          isMobile ? "w-8 h-8" : "w-10 h-10",
        )}
        style={{
          backgroundColor: variantConfig.bgColor,
          color: variantConfig.color,
        }}
      >
        {variantIcon}
      </div>
    ) : null;

    // Controls element
    const controlsElement = (
      <div
        className={cn(
          "flex items-center shrink-0",
          isMobile ? "gap-0.5" : "gap-1",
        )}
      >
        {minimizable && (
          <IconButton
            icon={<Minus size={18} />}
            onClick={handleMinimize}
            title={viewState === "minimized" ? "Restore" : "Minimize"}
          />
        )}
        {maximizable && (
          <IconButton
            icon={
              viewState === "maximized" ? (
                <Minimize2 size={18} />
              ) : (
                <Maximize2 size={18} />
              )
            }
            onClick={handleMaximize}
            title={viewState === "maximized" ? "Restore" : "Maximize"}
          />
        )}
        {closable && (
          <IconButton
            icon={<X size={20} />}
            onClick={handleClose}
            title="Close"
          />
        )}
      </div>
    );

    // For centered alignment, use three-column layout with spacer
    if (isCentered) {
      return (
        <div className="flex items-center justify-between w-full">
          {/* Left spacer - matches the width of right controls for centering */}
          <div
            className={cn(
              "flex items-center shrink-0",
              isMobile ? "gap-0.5 mr-2" : "gap-1 mr-4",
            )}
          >
            {iconElement}
            {/* Invisible spacer when no icon to balance the close button */}
            {!variantIcon && !minimizable && !maximizable && closable && (
              <div className={cn(isMobile ? "w-8" : "w-10")} />
            )}
          </div>
          {titleContent}
          <div className={cn(isMobile ? "ml-2" : "ml-4")}>
            {controlsElement}
          </div>
        </div>
      );
    }

    // For left/right alignment, use simpler two-part layout
    // When no subtitle, vertically center title and controls for proper alignment
    return (
      <div
        className={cn(
          "flex justify-between w-full gap-3",
          subtitle ? "items-start" : "items-center",
        )}
      >
        <div
          className={cn(
            "flex min-w-0 flex-1",
            subtitle ? "items-start" : "items-center",
            isMobile ? "gap-2" : "gap-3",
            isRightAligned && "order-2",
          )}
        >
          {iconElement}
          {titleContent}
        </div>
        <div
          className={cn(
            "shrink-0",
            subtitle && "pt-1",
            isRightAligned && "order-1",
          )}
        >
          {controlsElement}
        </div>
      </div>
    );
  }, [
    variantIcon,
    variantConfig,
    title,
    subtitle,
    headerClassName,
    minimizable,
    maximizable,
    closable,
    viewState,
    handleMinimize,
    handleMaximize,
    handleClose,
    isMobile,
    titleAlign,
  ]);

  // Custom footer
  const customFooter = useMemo(() => {
    if (footer === false || footer === null || !showFooter) {
      return null;
    }
    if (footer !== undefined) {
      return footer;
    }
    if (viewState === "minimized") {
      return null;
    }
    return (
      <DefaultFooter
        onCancel={handleCancel}
        onOk={onOk ? handleOk : undefined}
        cancelText={cancelText}
        okText={okText}
        confirmLoading={confirmLoading}
        cancelButtonProps={cancelButtonProps}
        okButtonProps={okButtonProps}
        variant={variant}
      />
    );
  }, [
    footer,
    showFooter,
    viewState,
    handleCancel,
    onOk,
    handleOk,
    cancelText,
    okText,
    confirmLoading,
    cancelButtonProps,
    okButtonProps,
    variant,
  ]);

  // Ant Design styles prop for semantic DOM styling
  const modalStyles = useMemo(() => {
    const headerPadding = isMobile
      ? "12px 16px"
      : isTablet
        ? "14px 20px"
        : isFullscreen
          ? "20px 24px"
          : "16px 24px";

    return {
      content: {
        padding: 0,
        borderRadius: isFullscreen || viewState === "maximized" ? 0 : 8,
        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column" as const,
        height:
          isFullscreen || viewState === "maximized"
            ? "100vh"
            : dimensions.height,
        minWidth: dimensions.minWidth,
        maxWidth: dimensions.maxWidth,
        margin: isCenter ? "0 auto" : undefined,
        ...incomingStyles?.content,
      },
      header:
        shouldUseCustomHeader || !showHeader
          ? {
              // Hide default header when using CustomHeader for fullscreen or showHeader is false
              display: "none",
            }
          : {
              padding: headerPadding,
              margin: 0,
              backgroundColor: colors.white,
              borderBottom: headerBorder
                ? `1px solid ${colors.surfaceSubtle}`
                : "none",
              flexShrink: 0,
              ...incomingStyles?.header,
            },
      body: {
        padding: shouldUseCustomHeader ? 0 : (bodyPadding ?? 0),
        backgroundColor: shouldUseCustomHeader
          ? "transparent"
          : bodyBackgroundColor || colors.white,
        flex: 1,
        overflowY: shouldUseCustomHeader
          ? ("hidden" as const)
          : ("auto" as const),
        overflowX: "hidden" as const,
        display: "flex",
        flexDirection: "column" as const,
        minHeight: 0,
        ...(!shouldUseCustomHeader ? bodyStyle : {}),
        ...incomingStyles?.body,
      },
      footer: {
        padding: 0,
        margin: 0,
        backgroundColor: "transparent",
        borderTop: "none",
        flexShrink: 0,
        ...incomingStyles?.footer,
      },
      mask: {
        backgroundColor: "rgba(0, 0, 0, 0.58)",
        ...incomingStyles?.mask,
      },
      wrapper: {
        overflow:
          isFullscreen || viewState === "maximized" ? "hidden" : undefined,
        ...incomingStyles?.wrapper,
      },
    };
  }, [
    isFullscreen,
    viewState,
    dimensions.height,
    dimensions.minWidth,
    dimensions.maxWidth,
    bodyStyle,
    bodyBackgroundColor,
    bodyPadding,
    showHeader,
    isMobile,
    isTablet,
    headerBorder,
    shouldUseCustomHeader,
    isCenter,
    incomingStyles,
  ]);

  // Theme configuration
  const themeConfig = useMemo(
    () => ({
      token: {
        colorPrimary: colors.primary,
        colorText: colors.textDefault,
        colorBorder: colors.surfaceSubtle,
        borderRadius: 8,
        fontFamily: "inherit",
        zIndexPopupBase: zIndex + 100,
      },
      components: {
        Modal: {
          contentBg: colors.white,
          headerBg: colors.white,
          titleColor: colors.textDarkest,
          titleFontSize: 18,
        },
      },
    }),
    [zIndex],
  );

  // Render content - with or without CustomHeader
  const renderContent = () => {
    if (shouldUseCustomHeader) {
      return (
        <div className="flex flex-col h-full">
          {renderCustomHeader()}
          <div
            className={cn("flex-1 overflow-auto", bodyClassName)}
            style={{
              backgroundColor: bodyBackgroundColor || colors.white,
              ...bodyStyle,
            }}
          >
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
                <div className="flex flex-col items-center gap-2">
                  <div
                    className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
                    style={{
                      borderColor: colors.primary,
                      borderTopColor: "transparent",
                    }}
                  />
                  <span className="text-sm" style={{ color: colors.textMuted }}>
                    Loading...
                  </span>
                </div>
              </div>
            )}
            {children}
          </div>
        </div>
      );
    }

    return (
      <div className={cn("relative flex-1 flex flex-col", bodyClassName)}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
            <div className="flex flex-col items-center gap-2">
              <div
                className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
                style={{
                  borderColor: colors.primary,
                  borderTopColor: "transparent",
                }}
              />
              <span className="text-sm" style={{ color: colors.textMuted }}>
                Loading...
              </span>
            </div>
          </div>
        )}
        {children}
      </div>
    );
  };

  // Determine footer - hide for fullscreen with CustomHeader (action is in header)
  const resolvedFooter = shouldUseCustomHeader ? null : customFooter ? (
    <div
      className={cn("px-6 py-3", footerClassName)}
      style={{
        backgroundColor: colors.surfaceLighter,
        borderTop: `1px solid ${colors.surfaceSubtle}`,
      }}
    >
      {customFooter}
    </div>
  ) : null;

  return (
    <ConfigProvider theme={themeConfig}>
      <AntModal
        open={open}
        onCancel={handleCancel}
        title={shouldUseCustomHeader || !showHeader ? null : customTitle}
        footer={resolvedFooter}
        width={dimensions.width}
        centered={!isFullscreen && viewState !== "maximized" && centered}
        closable={false}
        maskClosable={
          isFullscreen || viewState === "maximized" ? false : maskClosable
        }
        keyboard={isFullscreen || viewState === "maximized" ? false : keyboard}
        destroyOnHidden={destroyOnClose ?? destroyOnHidden}
        zIndex={zIndex}
        className={className}
        styles={modalStyles}
        style={
          isFullscreen || viewState === "maximized"
            ? {
                top: 0,
                maxWidth: "100vw",
                padding: 0,
                margin: 0,
              }
            : undefined
        }
        wrapClassName={
          isFullscreen || viewState === "maximized"
            ? "overflow-hidden"
            : undefined
        }
        transitionName=""
        maskTransitionName=""
        {...restProps}
      >
        {renderContent()}
      </AntModal>
    </ConfigProvider>
  );
};

export default Modal;
